import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Routes,
    Route,
    Navigate,
    useNavigate,
    useLocation,
} from 'react-router-dom';
import Keycloak from 'keycloak-js';
import keycloakConfig from '../utils/KeycloakConfig';
import LoadingPage from './LoadingPage/LoadingPage';
import LandingPage from './LandingPage/LandingPage';
import UserPage from './UserPage/UserPage';
import SupervisedUserPage from './SupervisedUserPage/SupervisedUserPage';
import HomePage from './HomePage/HomePage';
import ProfilePage from './ProfilePage/ProfilePage';
import RequestsPage from './RequestsPage/RequestsPage';
import CreateRequestPage from './CreateRequestPage/CreateRequestPage';
import RequestDetailPage from './RequestDetailPage/RequestDetailPage';
import RequestResultsPage from './RequestResultsPage/RequestResultsPage';
import RecertificationsPage from './RecertificationsPage/RecertificationsPage';
import ApprovalsPage from './ApprovalsPage/ApprovalsPage';
import ApprovalGroupDetailsPage from './ApprovalGroupDetailsPage/ApprovalGroupDetailsPage';
import EditProfilePage from './EditProfilePage/EditProfilePage';
import AdminPage from './AdminPage/AdminPage';
import { hasUnlockAdministratorAccess } from '../utils/AdminAccess';

const getCookieValue = (cookieName) => {
    const match = document.cookie
        .split(';')
        .map((cookie) => cookie.trim())
        .find((cookie) => cookie.startsWith(`${encodeURIComponent(cookieName)}=`));

    if (!match) {
        return null;
    }

    return decodeURIComponent(match.split('=').slice(1).join('='));
};

export function AppRoutes() {
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);
    const [authError, setAuthError] = useState('');
    const [authDetails, setAuthDetails] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isSessionExpired, setIsSessionExpired] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const keycloakInitPromiseRef = useRef(null);

    const keycloak = useMemo(() => new Keycloak(keycloakConfig), []);

    const buildAuthDetails = useCallback(() => {
        const cookieDetails = document.cookie
            .split(';')
            .map((cookie) => cookie.trim())
            .filter(Boolean)
            .reduce((accumulator, cookie) => {
                const [name, ...valueParts] = cookie.split('=');
                return {
                    ...accumulator,
                    [decodeURIComponent(name)]: decodeURIComponent(valueParts.join('=')),
                };
            }, {});

        return {
            cookies: cookieDetails,
            identity: {
                subject: keycloak.subject ?? null,
                sessionId: keycloak.sessionId ?? null,
                realm: keycloak.realm ?? null,
                clientId: keycloak.clientId ?? null,
                tokenParsed: keycloak.tokenParsed ?? null,
                idTokenParsed: keycloak.idTokenParsed ?? null,
                refreshTokenParsed: keycloak.refreshTokenParsed ?? null,
                realmAccess: keycloak.realmAccess ?? null,
                resourceAccess: keycloak.resourceAccess ?? null,
            },
        };
    }, [keycloak]);

    useEffect(() => {
        const currentPath = `${location.pathname}${location.search}`;
        if (currentPath && location.pathname !== '/') {
            sessionStorage.setItem('post_login_redirect', currentPath);
        }
    }, [location.pathname, location.search]);

    useEffect(() => {
        let isMounted = true;
        const storedToken = sessionStorage.getItem('kc_token');
        const storedRefreshToken = sessionStorage.getItem('kc_refresh_token');
        const storedIdToken = sessionStorage.getItem('kc_id_token');
        const storedSessionId = sessionStorage.getItem('kc_session');
        const existingToken = getCookieValue('kc_token') ?? storedToken;
        const urlParams = new URLSearchParams(window.location.search);
        const kcError = urlParams.get('error');
        const kcErrorDescription = urlParams.get('error_description');
        const sessionExpired = sessionStorage.getItem('session_expired') === 'true';

        if (sessionExpired) {
            setIsSessionExpired(true);
            sessionStorage.removeItem('session_expired');
        }

        if (kcError) {
            setAuthError(kcErrorDescription || 'We could not authenticate with JCWA ICAM.');
        }

        const initializeSession = () => {
            if (!keycloakInitPromiseRef.current) {
                keycloakInitPromiseRef.current = keycloak
                    .init({
                        token: existingToken ?? undefined,
                        refreshToken: storedRefreshToken ?? undefined,
                        idToken: storedIdToken ?? undefined,
                        checkLoginIframe: false,
                        pkceMethod: 'S256',
                    })
                    .then((authenticated) => {
                        if (!authenticated || keycloak.isTokenExpired()) {
                            return { authenticated: false };
                        }

                        return {
                            authenticated: true,
                            token: keycloak.token ?? existingToken ?? '',
                            refreshToken: keycloak.refreshToken ?? storedRefreshToken ?? '',
                            idToken: keycloak.idToken ?? storedIdToken ?? '',
                            sessionId: keycloak.sessionId ?? storedSessionId ?? '',
                            authDetails: buildAuthDetails(),
                        };
                    })
                    .catch((error) => {
                        console.warn('Keycloak token validation failed', error);
                        return { authenticated: false };
                    });
            }

            return keycloakInitPromiseRef.current;
        };

        initializeSession().then((result) => {
            if (!isMounted) {
                return;
            }

            if (result.authenticated) {
                sessionStorage.setItem('kc_token', result.token);
                sessionStorage.setItem('kc_refresh_token', result.refreshToken);
                sessionStorage.setItem('kc_id_token', result.idToken);
                sessionStorage.setItem('kc_session', result.sessionId);
                setAuthDetails(result.authDetails);
                setIsAuthenticated(true);
            }

            setIsCheckingAuth(false);
        });

        return () => {
            isMounted = false;
        };
    }, [buildAuthDetails, keycloak]);

    const handleLogin = async () => {
        setAuthError('');
        setIsSessionExpired(false);
        setIsAuthenticating(true);
        const postLoginRedirect =
            sessionStorage.getItem('post_login_redirect') || '/home';

        try {
            await keycloak.login({
                redirectUri: `${window.location.origin}${postLoginRedirect}`,
                prompt: 'login',
            });
        } catch (error) {
            console.error('Keycloak login failed', error);
            setAuthError('We could not connect to JCWA ICAM. Please try again.');
        } finally {
            setIsAuthenticating(false);
        }
    };

    const handleSignOut = () => {
        const cookieNames = document.cookie
            .split(';')
            .map((cookie) => cookie.trim())
            .filter(Boolean)
            .map((cookie) => cookie.split('=')[0])
            .filter((name) => name.startsWith('kc_'));

        cookieNames.forEach((name) => {
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        });

        sessionStorage.removeItem('kc_token');
        sessionStorage.removeItem('kc_refresh_token');
        sessionStorage.removeItem('kc_id_token');
        sessionStorage.removeItem('kc_session');
        sessionStorage.removeItem('post_login_redirect');
        sessionStorage.removeItem('session_expired');

        setAuthDetails(null);
        setIsAuthenticated(false);
        setAuthError('');
        navigate('/');
    };

    const postLoginRedirect = sessionStorage.getItem('post_login_redirect');
    const authenticatedLandingPath =
        postLoginRedirect && postLoginRedirect !== '/' ? postLoginRedirect : '/home';
    const isUnlockAdministrator = hasUnlockAdministratorAccess(
        authDetails?.identity?.tokenParsed?.groups
    );

    return (
        <Routes>
            <Route
                path="/"
                element={
                    isCheckingAuth ? (
                        <LoadingPage />
                    ) : isAuthenticated && authDetails ? (
                        <Navigate to={authenticatedLandingPath} replace />
                    ) : (
                        <LandingPage
                            authError={authError}
                            isAuthenticating={isAuthenticating}
                            onLogin={handleLogin}
                            sessionExpired={isSessionExpired}
                        />
                    )
                }
            />
            <Route
                path="/home"
                element={
                    isCheckingAuth ? (
                        <LoadingPage />
                    ) : isAuthenticated && authDetails ? (
                        <HomePage authDetails={authDetails} onSignOut={handleSignOut} />
                    ) : (
                        <Navigate to="/" replace />
                    )
                }
            />
            <Route
                path="/user"
                element={
                    isCheckingAuth ? (
                        <LoadingPage />
                    ) : isAuthenticated && authDetails ? (
                        <UserPage authDetails={authDetails} onSignOut={handleSignOut} />
                    ) : (
                        <Navigate to="/" replace />
                    )
                }
            />
            <Route
                path="/users/:uuid"
                element={
                    isCheckingAuth ? (
                        <LoadingPage />
                    ) : isAuthenticated && authDetails ? (
                        <SupervisedUserPage authDetails={authDetails} onSignOut={handleSignOut} />
                    ) : (
                        <Navigate to="/" replace />
                    )
                }
            />
            <Route
                path="/profile"
                element={
                    isCheckingAuth ? (
                        <LoadingPage />
                    ) : isAuthenticated && authDetails ? (
                        <ProfilePage authDetails={authDetails} onSignOut={handleSignOut} />
                    ) : (
                        <Navigate to="/" replace />
                    )
                }
            />
            <Route
                path="/editProfile"
                element={
                    isCheckingAuth ? (
                        <LoadingPage />
                    ) : isAuthenticated && authDetails ? (
                        <EditProfilePage authDetails={authDetails} onSignOut={handleSignOut} />
                    ) : (
                        <Navigate to="/" replace />
                    )
                }
            />
            <Route
                path="/requests"
                element={
                    isCheckingAuth ? (
                        <LoadingPage />
                    ) : isAuthenticated && authDetails ? (
                        <RequestsPage authDetails={authDetails} onSignOut={handleSignOut} />
                    ) : (
                        <Navigate to="/" replace />
                    )
                }
            />
            <Route
                path="/createRequest"
                element={
                    isCheckingAuth ? (
                        <LoadingPage />
                    ) : isAuthenticated && authDetails ? (
                        <CreateRequestPage authDetails={authDetails} onSignOut={handleSignOut} />
                    ) : (
                        <Navigate to="/" replace />
                    )
                }
            />
            <Route
                path="/requests/:requestId"
                element={
                    isCheckingAuth ? (
                        <LoadingPage />
                    ) : isAuthenticated && authDetails ? (
                        <RequestDetailPage authDetails={authDetails} onSignOut={handleSignOut} />
                    ) : (
                        <Navigate to="/" replace />
                    )
                }
            />
            <Route
                path="/requestResults"
                element={
                    isCheckingAuth ? (
                        <LoadingPage />
                    ) : isAuthenticated && authDetails ? (
                        <RequestResultsPage authDetails={authDetails} onSignOut={handleSignOut} />
                    ) : (
                        <Navigate to="/" replace />
                    )
                }
            />
            <Route
                path="/recertifications"
                element={
                    isCheckingAuth ? (
                        <LoadingPage />
                    ) : isAuthenticated && authDetails ? (
                        <RecertificationsPage authDetails={authDetails} onSignOut={handleSignOut} />
                    ) : (
                        <Navigate to="/" replace />
                    )
                }
            />
            <Route
                path="/approvals"
                element={
                    isCheckingAuth ? (
                        <LoadingPage />
                    ) : isAuthenticated && authDetails ? (
                        <ApprovalsPage authDetails={authDetails} onSignOut={handleSignOut} />
                    ) : (
                        <Navigate to="/" replace />
                    )
                }
            />
            <Route
                path="/ApprovalGroupDetails/:uuid"
                element={
                    isCheckingAuth ? (
                        <LoadingPage />
                    ) : isAuthenticated && authDetails ? (
                        <ApprovalGroupDetailsPage authDetails={authDetails} onSignOut={handleSignOut} />
                    ) : (
                        <Navigate to="/" replace />
                    )
                }
            />
            <Route
                path="/admin"
                element={
                    isCheckingAuth ? (
                        <LoadingPage />
                    ) : isAuthenticated && authDetails ? (
                        isUnlockAdministrator ? (
                            <AdminPage authDetails={authDetails} onSignOut={handleSignOut} />
                        ) : (
                            <Navigate to="/home" replace />
                        )
                    ) : (
                        <Navigate to="/" replace />
                    )
                }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

export default AppRoutes;
